"use client"

import {Separator} from "@/components/ui/separator";
import {SidebarTrigger} from "@/components/ui/sidebar";
import {useLocation} from "react-router-dom";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {Button} from "@/components/ui/button";
import {Trash, Clock, Calendar, Trash2, Check, X} from 'lucide-react';
import {Status, StatusIndicator, StatusLabel} from '@/components/ui/shadcn-io/status';
import {useTranslation} from 'react-i18next';
import {useEffect, useState} from "react";
import axiosInstance from "@/api/axios";

// Enhanced BinObject interface to include status
interface BinObject {
    id: string;
    is_garby_vision?: boolean;
    status?: 'online' | 'offline' | 'maintenance' | 'degraded';
    last_update?: string;

    [key: string]: any;
}

// Type for bins that can be either strings or objects with an id
type BinType = string | BinObject;

interface SiteHeaderProps {
    timeRangeDropdownOpen?: boolean;
    setTimeRangeDropdownOpen?: (open: boolean) => void;
    binDropdownOpen?: boolean;
    setBinDropdownOpen?: (open: boolean) => void;
    selectedTimeRange?: string;
    selectTimeRange?: (timeRange: string) => boolean;
    timeRangeOptions?: string[];
    selectedBins?: string[];
    allBins?: BinObject[]; // Updated to use enhanced BinObject
    toggleBinSelection?: (bin: string) => boolean;
    yearDropdownOpen?: boolean;
    setYearDropdownOpen?: (open: boolean) => void;
    selectedYear?: string;
    selectYear?: (year: string) => boolean;
    yearOptions?: string[];
    wasteTypeDropdownOpen?: boolean;
    setWasteTypeDropdownOpen?: (open: boolean) => void;
    selectedWasteTypes?: string[];
    toggleWasteTypeSelection?: (wasteType: string) => boolean;
    wasteTypeOptions?: string[];
}

export function SiteHeader({
                               timeRangeDropdownOpen,
                               setTimeRangeDropdownOpen,
                               binDropdownOpen,
                               setBinDropdownOpen,
                               selectedTimeRange,
                               selectTimeRange,
                               timeRangeOptions,
                               selectedBins,
                               allBins,
                               toggleBinSelection,
                               yearDropdownOpen,
                               setYearDropdownOpen,
                               selectedYear,
                               selectYear,
                               yearOptions,
                               wasteTypeDropdownOpen,
                               setWasteTypeDropdownOpen,
                               selectedWasteTypes,
                               toggleWasteTypeSelection,
                               wasteTypeOptions,
                           }: SiteHeaderProps) {
    const location = useLocation();
    const {t} = useTranslation();
    const [binsWithStatus, setBinsWithStatus] = useState<BinObject[]>([]);

    // Single effect to handle all bin status fetching with polling
    useEffect(() => {
        const fetchBinStatuses = async () => {
            try {
                const response = await axiosInstance.get("bins-with-status");
                const binsWithStatus = response.data || [];

                const validBins = binsWithStatus.filter((bin: any): bin is BinObject =>
                    bin &&
                    typeof bin === 'object' &&
                    bin.id &&
                    typeof bin.id === 'string'
                );

                setBinsWithStatus(validBins);
            } catch (error) {
                console.error("Error fetching bin statuses:", error);
                setBinsWithStatus([]);
            }
        };

        // Initial fetch when component mounts
        fetchBinStatuses();

        // Set up polling every 5 minutes for automatic refresh
        const interval = setInterval(fetchBinStatuses, 300000);

        // Cleanup interval on unmount
        return () => clearInterval(interval);
    }, []); // Empty dependency array - only run once on mount

    // Separate effect ONLY for immediate refresh when dropdown opens
    useEffect(() => {
        const fetchBinStatusesForDropdown = async () => {
            try {
                const response = await axiosInstance.get("bins-with-status");
                const binsWithStatus = response.data || [];

                const validBins = binsWithStatus.filter((bin: any): bin is BinObject =>
                    bin &&
                    typeof bin === 'object' &&
                    bin.id &&
                    typeof bin.id === 'string'
                );

                setBinsWithStatus(validBins);
            } catch (error) {
                console.error("Error fetching bin statuses:", error);
                setBinsWithStatus([]);
            }
        };

        // Only fetch immediately when dropdown opens (no interval here)
        if (binDropdownOpen) {
            fetchBinStatusesForDropdown();
        }
    }, [binDropdownOpen]); // Only depends on binDropdownOpen


    // Use bins with status if available, otherwise fall back to allBins
    const binsToDisplay = binsWithStatus.length > 0 ? binsWithStatus : (allBins || []);

    const getHeaderTitle = () => {
        const pathParts = location.pathname.split('/').filter(Boolean);
        if (pathParts.length === 0) return t('navigation.dashboard');

        // Hide title for account/security page
        if (location.pathname.includes('/account/security')) {
            return '';
        }

        const lastPart = pathParts[pathParts.length - 1];

        const routeTitles: Record<string, string> = {
            'profile': t('navigation.profile'),
            'circularity': t('navigation.circularity'),
            'recycling': t('navigation.recyclingDashboard'),
            'documents': t('navigation.documents'),
            'login': t('navigation.login'),
            'signup': t('navigation.signup'),
            'thanks': t('navigation.thankYou'),
            'logout': t('navigation.logout'),
            'cost-savings': t('navigation.costSavings'),
            'homepage': t('navigation.homePage'),
            'operations': t('navigation.operations'),
        };

        return routeTitles[lastPart] ||
            lastPart.charAt(0).toUpperCase() + lastPart.slice(1);
    };

    const getTimeRangeTranslation = (timeRange: string) => {
        const translations: Record<string, string> = {
            'Day': t('timeRanges.Day'),
            'Week': t('timeRanges.Week'),
            'Month': t('timeRanges.Month'),
            '3Month': t('timeRanges.3Month'),
            '3 Months': t('timeRanges.3Month'), // Map the actual value to the translation key
            '6Month': t('timeRanges.6Month'),
            '6 Months': t('timeRanges.6Month'), // Map the actual value to the translation key
            'Year': t('timeRanges.Year')
        };

        return translations[timeRange] || timeRange;
    };

    const isDashboardRoute = () => {
        return location.pathname === "/" ||
            location.pathname === "/operations" ||
            location.pathname.includes("dashboard");
    };

    // Select all bins
    const selectAllBins = () => {
        if (binsToDisplay && toggleBinSelection) {
            binsToDisplay.forEach(bin => {
                const binId = typeof bin === 'object' && bin !== null ? bin.id : bin;
                if (!selectedBins?.includes(binId)) {
                    toggleBinSelection(binId);
                }
            });
        }
    };


    // Clear all bin selections
    const clearAllBins = () => {
        if (selectedBins && toggleBinSelection) {
            selectedBins.forEach(bin => {
                toggleBinSelection(bin);
            });
        }
    };

    return (
        <header
            className="group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 flex h-12 shrink-0 items-center gap-2 transition-[width,height] ease-linear sticky top-0 backdrop-blur-md z-30 border-b w-full max-w-[100vw] overflow-x-hidden px-2 sm:px-4">
            <div className="flex w-full items-center justify-between gap-1">
                <div className="flex items-center min-w-0 max-w-[60%] sm:max-w-none">
                    <SidebarTrigger className="-ml-1 h-8 w-8 sm:h-9 sm:w-9"/>
                    <Separator
                        orientation="vertical"
                        className="mx-1 sm:mx-2 data-[orientation=vertical]:h-4"
                    />
                    <h1 className="text-sm sm:text-base font-medium truncate">{getHeaderTitle()}</h1>
                </div>

                {isDashboardRoute() && (
                    <div className="flex gap-2">
                        {/* Year Selection Dropdown - Disabled */}
                        {yearOptions && selectedYear && selectYear && (
                            <div className="hidden sm:block">
                                <DropdownMenu modal={false} open={yearDropdownOpen} onOpenChange={setYearDropdownOpen}>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline" size="sm" disabled
                                                className="opacity-50 cursor-not-allowed">
                                            <Calendar className="h-4 w-4 sm:mr-2 opacity-50"/>
                                            <span
                                                className="hidden sm:inline">{t('header.filters.year')}: {selectedYear}</span>
                                            <span className="sm:hidden">{selectedYear}</span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className="w-56 opacity-50 pointer-events-none">
                                        <DropdownMenuLabel>{t('header.filters.selectYear')}</DropdownMenuLabel>
                                        <DropdownMenuSeparator/>
                                        {yearOptions.map(year => (
                                            <DropdownMenuCheckboxItem
                                                key={year}
                                                checked={year === selectedYear}
                                                disabled
                                                className="cursor-not-allowed"
                                            >
                                                {year}
                                            </DropdownMenuCheckboxItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        )}

                        {wasteTypeOptions && selectedWasteTypes && toggleWasteTypeSelection && (
                            <div className="hidden sm:block"> {/* Show only on desktop, hidden on mobile */}
                                <DropdownMenu modal={false} open={wasteTypeDropdownOpen}
                                              onOpenChange={setWasteTypeDropdownOpen}>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline" size="sm" disabled
                                                className="opacity-50 cursor-not-allowed">
                                            <Trash2 className="h-4 w-4 sm:mr-2"/>
                                            <span
                                                className="hidden sm:inline">{t('header.filters.waste')}: {selectedWasteTypes.length}</span>
                                            <span className="sm:hidden">{selectedWasteTypes.length}</span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className="w-56 opacity-50 pointer-events-none">
                                        <div className="flex items-center justify-between px-2 py-1.5">
                                            <DropdownMenuLabel
                                                className="py-0">{t('header.filters.wasteTypes')}</DropdownMenuLabel>
                                            <div className="flex gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 px-2 text-xs"
                                                    disabled
                                                >
                                                    <Check className="h-3 w-3 mr-1"/>
                                                    {t('common.all')}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 px-2 text-xs"
                                                    disabled
                                                >
                                                    <X className="h-3 w-3 mr-1"/>
                                                    {t('common.clear')}
                                                </Button>
                                            </div>
                                        </div>
                                        <DropdownMenuSeparator/>
                                        {wasteTypeOptions.map(wasteType => (
                                            <DropdownMenuCheckboxItem
                                                key={wasteType}
                                                checked={selectedWasteTypes.includes(wasteType)}
                                                disabled
                                                className="cursor-not-allowed"
                                            >
                                                {wasteType}
                                            </DropdownMenuCheckboxItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        )}
                        {/* Bin Selection Dropdown with Status Indicators */}
                        {selectedBins && toggleBinSelection && (
                            <DropdownMenu modal={false} open={binDropdownOpen} onOpenChange={setBinDropdownOpen}>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm">
                                        <Trash className="h-4 w-4 sm:mr-2 opacity-50"/>
                                        <span
                                            className="hidden sm:inline">{t('header.filters.bins')}: {selectedBins.length}</span>
                                        <span className="sm:hidden">{selectedBins.length}</span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="w-64">
                                    <div className="flex items-center justify-between px-2 py-1.5">
                                        <DropdownMenuLabel
                                            className="py-0">{t('header.filters.selected')}</DropdownMenuLabel>
                                        <div className="flex gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 px-2 text-xs"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    selectAllBins();
                                                }}
                                            >
                                                <Check className="h-3 w-3 mr-1"/>
                                                {t('common.all')}
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 px-2 text-xs"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    clearAllBins();
                                                }}
                                            >
                                                <X className="h-3 w-3 mr-1"/>
                                                {t('common.clear')}
                                            </Button>
                                        </div>
                                    </div>
                                    <DropdownMenuSeparator/>
                                    {binsToDisplay && [...binsToDisplay]
                                        .filter(bin => bin != null) // Filter out null/undefined bins
                                        .sort((a, b) => {
                                            // Safely extract bin IDs with proper null checks
                                            const binA = typeof a === 'object' && a ? a.id : a;
                                            const binB = typeof b === 'object' && b ? b.id : b;

                                            // Additional safety check - ensure both binA and binB are strings
                                            if (!binA || !binB) return 0;

                                            // Convert to strings to be safe
                                            const binAStr = String(binA);
                                            const binBStr = String(binB);

                                            const nameA = binAStr.replace(/\d+/g, '').toLowerCase();
                                            const nameB = binBStr.replace(/\d+/g, '').toLowerCase();

                                            if (nameA < nameB) return -1;
                                            if (nameA > nameB) return 1;

                                            const numA = parseInt(binAStr.match(/\d+/)?.[0] || '0');
                                            const numB = parseInt(binBStr.match(/\d+/)?.[0] || '0');

                                            return numA - numB;
                                        })
                                        .map(bin => {
                                            // Safe extraction of bin properties
                                            const binId = typeof bin === 'object' && bin ? bin.id : bin;
                                            const binStatus = typeof bin === 'object' && bin ? bin.status || 'offline' : 'offline';

                                            // Skip if binId is still undefined/null
                                            if (!binId) return null;

                                            return (
                                                <DropdownMenuCheckboxItem
                                                    key={binId}
                                                    checked={selectedBins.includes(binId)}
                                                    onSelect={(e) => {
                                                        e.preventDefault();
                                                        toggleBinSelection(binId);
                                                    }}
                                                    className="flex items-center justify-between pr-2"
                                                >

                                                    <div className="flex items-center gap-2 flex-1">
                                                        <span>{binId}</span>
                                                        <Status status={binStatus} className="ml-auto">
                                                            <StatusIndicator/>
                                                        </Status>
                                                    </div>
                                                </DropdownMenuCheckboxItem>
                                            );
                                        })
                                        .filter(item => item !== null) // Filter out any null items
                                    }
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Time Range Selection Dropdown */}
                        {timeRangeOptions && (
                            <DropdownMenu modal={false} open={timeRangeDropdownOpen}
                                          onOpenChange={setTimeRangeDropdownOpen}>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm">
                                        <Clock className="h-4 w-4 sm:mr-2 opacity-50"/>
                                        <span
                                            className="hidden sm:inline">{t('header.filters.period')}: {selectedTimeRange ? getTimeRangeTranslation(selectedTimeRange) : selectedTimeRange}</span>
                                        <span
                                            className="sm:hidden">{selectedTimeRange ? getTimeRangeTranslation(selectedTimeRange) : selectedTimeRange}</span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="w-56">
                                    <DropdownMenuLabel>{t('header.filters.selectTimePeriod')}</DropdownMenuLabel>
                                    <DropdownMenuSeparator/>
                                    {timeRangeOptions.map(timeRange => (
                                        <DropdownMenuCheckboxItem
                                            key={timeRange}
                                            checked={timeRange === selectedTimeRange}
                                            onSelect={(e) => {
                                                e.preventDefault();
                                                selectTimeRange?.(timeRange);
                                            }}
                                        >
                                            {getTimeRangeTranslation(timeRange)}
                                        </DropdownMenuCheckboxItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                )}
            </div>
        </header>
    );
}